"use client";
import { useEffect, useRef, useState } from "react";

export default function StackedCards({ children, className = "" }) {
	const containerRef = useRef(null);
	const [cardStates, setCardStates] = useState([]);
	const [maxHeight, setMaxHeight] = useState(0);

	useEffect(() => {
		if (!containerRef.current) return;

		const cards = Array.from(containerRef.current.children);
		let animationId;

		const updateCards = () => {
			// Calculer la hauteur maximale des cartes
			const heights = cards.map((card) => card.scrollHeight);
			const currentMaxHeight = Math.max(...heights);

			if (currentMaxHeight !== maxHeight) {
				setMaxHeight(currentMaxHeight);
			}

			const newCardStates = cards.map((card, index) => {
				const rect = card.getBoundingClientRect();
				const stickyTop = 100 + index * 10; // Offset de base 100px + 10px par carte

				// Une carte est stackée si elle a atteint son sticky top
				const isStacked = rect.top <= stickyTop;

				return {
					index,
					isStacked,
				};
			});

			setCardStates(newCardStates);
			animationId = requestAnimationFrame(updateCards);
		};

		updateCards();

		return () => {
			if (animationId) {
				cancelAnimationFrame(animationId);
			}
		};
	}, [children]);

	return (
		<div ref={containerRef} className={`stacked-cards ${className}`}>
			{children.map((child, index) => {
				const cardState = cardStates[index] || { isStacked: false };
				const isLastCard = index === children.length - 1;

				// Calculer le nombre de cartes stackées après cette carte
				const stackedCardsAfter = cardStates.slice(index + 1).filter((state) => state && state.isStacked).length;

				// Réduction de scale et opacité : s'applique à la carte courante si stackée
				let scaleReduction = 0;
				let opacityReduction = 0;
				if (cardState.isStacked && !isLastCard) {
					scaleReduction = 0.1 + stackedCardsAfter * 0.1;
					opacityReduction = 0.33 + stackedCardsAfter * 0.33;
				}

				const scale = 1 - scaleReduction;
				const opacity = 1 - opacityReduction;

				return (
					<div
						key={index}
						className={`stacked-card ${cardState.isStacked ? "is-stacked" : ""}`}
						style={{
							position: "sticky",
							top: `${100 + index * 10}px`, // Offset de base 100px + 10px par carte
							zIndex: children.length + index, // z-index décroissant
							transform: `scale(${scale})`,
							transformOrigin: "top center",
							height: maxHeight > 0 ? `${maxHeight}px` : "auto",
							transition: "transform 0.3s ease",
						}}
					>
						<div style={{ opacity: opacity, transition: "opacity 0.15s ease" }}>
							{child}
						</div>
					</div>
				);
			})}
		</div>
	);
}
